<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="560" persistent>
    <v-card rounded="xl">
      <v-card-title class="pa-5 pb-3">
        <div class="d-flex align-center justify-space-between">
          <div>
            <h2 class="dialog-title">{{ isComms ? 'Add Comms Agent' : 'Add Worker Agent' }}</h2>
            <p v-if="!noSlotsBlocking && step < 3" class="text-caption text-medium-emphasis mt-1">Step {{ step }} of 2</p>
          </div>
          <v-btn icon="mdi-close" variant="text" @click="close" />
        </div>
      </v-card-title>

      <!-- Slot check loading -->
      <template v-if="slotCheckLoading">
        <v-card-text class="pa-8 text-center">
          <v-progress-circular indeterminate color="#667de8" size="36" />
          <p class="text-body-2 text-medium-emphasis mt-3">Checking available slots…</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" rounded="lg" @click="close">Cancel</v-btn>
        </v-card-actions>
      </template>

      <!-- No slots available -->
      <template v-else-if="noSlotsBlocking">
        <v-card-text class="pa-6 text-center">
          <v-avatar color="#fff3e0" size="60" rounded="xl" class="mb-4">
            <v-icon color="orange" size="30">mdi-ticket-outline</v-icon>
          </v-avatar>
          <h3 class="font-weight-bold mb-2" style="font-size:1.1rem">No agent slots available</h3>
          <p class="text-body-2 text-medium-emphasis mb-4">
            All purchased slots are in use. Purchase additional slots to create another agent.
          </p>
          <v-chip color="#667de8" variant="tonal" size="small" class="mb-1">
            {{ slotInfo.slotsUsed }} / {{ slotInfo.slotsPurchased }} slots in use
          </v-chip>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" rounded="lg" @click="close">Close</v-btn>
          <v-btn color="#667de8" variant="flat" rounded="lg" prepend-icon="mdi-cart-outline" @click="goToBilling">
            Purchase Slots
          </v-btn>
        </v-card-actions>
      </template>

      <!-- Normal form -->
      <template v-else>

        <!-- Step indicator -->
        <div class="px-5 pb-2">
          <v-progress-linear :model-value="(step / 2) * 100" color="#667de8" rounded height="4" />
          <div class="d-flex justify-space-between mt-1">
            <span v-for="(label, i) in stepLabels" :key="i"
              class="text-caption"
              :style="{ color: step > i ? '#667de8' : '#aaa', fontWeight: step === i + 1 ? '700' : '400' }">
              {{ label }}
            </span>
          </div>
        </div>

        <v-divider class="mt-2" />

        <v-card-text class="pa-5">

          <!-- ── Step 1: Identity + Role ── -->
          <v-form v-if="step === 1" ref="form1">

            <!-- Agent type toggle -->
            <div class="field-label">Agent Type</div>
            <v-btn-toggle v-model="form.agentType" mandatory density="comfortable" rounded="lg"
              color="#667de8" variant="outlined" class="mb-4 w-100">
              <v-btn value="worker" style="flex:1">
                <v-icon start size="16">mdi-robot-outline</v-icon>Worker
              </v-btn>
              <v-btn value="comms" style="flex:1">
                <v-icon start size="16">mdi-antenna</v-icon>Comms
              </v-btn>
            </v-btn-toggle>

            <v-alert v-if="isComms" type="info" variant="tonal" density="compact" rounded="lg" class="mb-4" style="font-size:0.8rem">
              One comms agent per school. It receives all inbound WhatsApp messages and routes them to worker agents.
            </v-alert>

            <div class="field-label">Agent Name</div>
            <v-text-field v-model="form.name" variant="outlined" density="comfortable" rounded="lg"
              :placeholder="isComms ? 'e.g. Herzlia Comms' : 'e.g. Grade 4A ClassMom'" :rules="[required]" class="mb-4" />

            <!-- Worker-only fields -->
            <template v-if="!isComms">
              <div class="field-label">Category</div>
              <v-select v-model="form.category" :items="categories" item-title="label" item-value="value"
                variant="outlined" density="comfortable" rounded="lg" placeholder="Select category"
                :rules="[required]" class="mb-4" />

              <div class="field-label">Group / Class Name</div>
              <v-text-field v-model="form.groupName" variant="outlined" density="comfortable" rounded="lg"
                :placeholder="groupPlaceholder" :rules="[required]" class="mb-4" />

              <div class="field-label">Virtual Groups <span class="text-medium-emphasis font-weight-regular">(optional)</span></div>
              <v-autocomplete
                v-model="form.agentGroupIds"
                v-model:search="groupSearch"
                :items="groupItems"
                item-title="name"
                item-value="id"
                multiple
                chips
                closable-chips
                variant="outlined"
                density="comfortable"
                rounded="lg"
                placeholder="e.g. Grade 6 Group"
                hint="Group this agent with others for shared file uploads."
                persistent-hint
                :loading="groupsLoading"
                no-data-text="No groups yet — type to create one"
                class="mb-4"
              >
                <template #append-item>
                  <v-list-item
                    v-if="groupSearch && !groupItems.some(g => g.name.toLowerCase() === groupSearch.toLowerCase())"
                    :title="`Create &quot;${groupSearch}&quot;`"
                    prepend-icon="mdi-plus"
                    @click="createAndSelectGroup(groupSearch)"
                  />
                </template>
              </v-autocomplete>
            </template>

            <!-- Comms-only field -->
            <template v-if="isComms">
              <div class="field-label">WhatsApp Account</div>
              <v-select
                v-model="form.waPhoneNumberId"
                :items="waAccounts"
                item-title="display_name"
                item-value="phone_number_id"
                :loading="waAccountsLoading"
                :no-data-text="waAccounts.length === 0 ? 'No WhatsApp accounts configured — add one in School Settings first' : 'No accounts'"
                variant="outlined" density="comfortable" rounded="lg" :rules="[required]"
                placeholder="Select a WhatsApp Business account"
                persistent-hint class="mb-4" />
            </template>

            <div class="field-label">Role Brief</div>
            <v-textarea v-model="form.roleBrief" variant="outlined" density="comfortable" rounded="lg"
              rows="3" auto-grow
              :placeholder="isComms
                ? 'e.g. You are the main communication hub for this school. You receive all parent WhatsApp messages and route them to the correct class agent.'
                : 'e.g. You are in charge of helping with the Grade 4A class. You assist parents with homework queries, event dates, and class-specific reminders.'"
              hint="A short description of this agent's purpose. Used to build its default instructions."
              persistent-hint class="mb-0" />
          </v-form>

          <!-- ── Step 2: AI Engine ── -->
          <v-form v-if="step === 2" ref="form2">
            <v-alert type="info" variant="tonal" density="compact" rounded="lg" class="mb-4">
              Select the AI model for this agent. Leave the API key blank to use the platform default.
            </v-alert>

            <div class="field-label">LLM Provider</div>
            <v-select v-model="form.llm_provider" :items="llmProviders" item-title="label" item-value="value"
              variant="outlined" density="comfortable" rounded="lg" :rules="[required]" class="mb-4"
              @update:model-value="form.llm_model = ''" />

            <div class="field-label">Model</div>
            <v-select v-model="form.llm_model" :items="availableModels" item-title="label" item-value="value"
              variant="outlined" density="comfortable" rounded="lg" :rules="[required]" class="mb-4" />

            <div class="field-label">API Key <span class="text-medium-emphasis font-weight-regular">(optional)</span></div>
            <v-text-field v-model="form.api_key" variant="outlined" density="comfortable" rounded="lg"
              :placeholder="apiKeyPlaceholder"
              :type="showApiKey ? 'text' : 'password'"
              :append-inner-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showApiKey = !showApiKey"
              hint="Leave blank to use the server environment key."
              persistent-hint class="mb-3" />

            <v-alert v-if="createError" type="error" variant="tonal" rounded="lg" density="compact">
              {{ createError }}
            </v-alert>
          </v-form>

          <!-- ── Step 3: Done ── -->
          <div v-if="step === 3" class="text-center py-4">
            <v-avatar color="#e8f5e9" size="60" rounded="xl" class="mb-4">
              <v-icon color="success" size="30">mdi-check-circle-outline</v-icon>
            </v-avatar>
            <h3 class="font-weight-bold mb-2" style="font-size:1.1rem">{{ form.name }} created!</h3>
            <p class="text-body-2 text-medium-emphasis mb-4">
              The agent is ready. Add members, upload files, and configure reminders from the agent detail page.
            </p>
            <v-chip color="#667de8" variant="tonal" size="small" prepend-icon="mdi-robot-outline">
              {{ isComms ? 'comms · School Communications' : `${form.category} · ${form.groupName}` }}
            </v-chip>
          </div>

        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn v-if="step === 2" variant="text" rounded="lg" prepend-icon="mdi-arrow-left" @click="step--">Back</v-btn>
          <v-spacer />
          <v-btn variant="text" rounded="lg" @click="close">{{ step === 3 ? 'Close' : 'Cancel' }}</v-btn>

          <v-btn v-if="step === 1" color="#667de8" variant="flat" rounded="lg" append-icon="mdi-arrow-right" @click="nextStep">
            Next
          </v-btn>
          <v-btn v-if="step === 2" color="#667de8" variant="flat" rounded="lg" prepend-icon="mdi-robot-outline"
            :loading="loading" @click="submit">
            Create Agent
          </v-btn>
          <v-btn v-if="step === 3" color="#667de8" variant="flat" rounded="lg" prepend-icon="mdi-arrow-right" @click="finish">
            Go to Agent
          </v-btn>
        </v-card-actions>

      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

function buildDefaultCommsPrompt(name, roleBrief) {
  const brief = roleBrief?.trim()
    ? roleBrief.trim()
    : 'You are the main communication hub for this school, handling all inbound WhatsApp messages from parents.'

  return `You are ${name}, the school communications agent.

${brief}

## Core responsibilities

1. **Message routing** — Identify the sending parent, determine which worker agents they belong to, and coordinate responses across them.

2. **Context aggregation** — Gather relevant information from the parent's worker agents (calendar, files, reminders) before responding.

3. **24h service window** — Track active conversation windows. If outside a window, use approved templates to re-engage.

4. **General enquiries** — Handle school-wide questions that don't belong to a specific class or group.

## Behaviour guidelines

- Be friendly, professional, and concise.
- Always respond in the language the parent is using.
- Never share personal information about other parents or students.
- If you are unsure, say so — do not guess.`
}

function buildDefaultSystemPrompt(name, groupName, category, roleBrief) {
  const categoryLabel = { class: 'class', grade: 'grade', sport: 'sport team', extramural: 'extramural group' }[category] || 'group'
  const brief = roleBrief?.trim()
    ? roleBrief.trim()
    : `You assist parents and staff associated with the ${groupName} ${categoryLabel}.`

  return `You are ${name}, a worker agent for the ${groupName} ${categoryLabel}.

${brief}

## Core responsibilities

1. **Member verification** — Before sharing any information, confirm that the requesting phone number belongs to a registered member of this agent. Do not respond to unregistered numbers.

2. **Calendar & events** — You have access to this group's calendar. Use it to answer questions about upcoming events, dates, activities, or deadlines specific to ${groupName}.

3. **Files & knowledge base** — You have access to documents uploaded for this group. Use them to answer relevant questions accurately.

4. **Reminders** — You can surface pending reminders relevant to the group or the requesting parent.

5. **Escalation** — For school-wide matters outside your scope, advise the parent to contact the school directly or use the main school WhatsApp line.

## Behaviour guidelines

- Be friendly, helpful, and concise.
- Always respond in the language the parent is using.
- Never share another parent's personal information.
- If you are unsure, say so — do not guess.`
}

export default {
  name: 'AddClassMomDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    schoolId: { type: [Number, String], default: null }
  },
  emits: ['update:modelValue', 'created'],
  setup(props, { emit }) {
    const router = useRouter()
    const step       = ref(1)
    const form1      = ref(null)
    const form2      = ref(null)
    const loading    = ref(false)
    const showApiKey = ref(false)
    const createdAgent   = ref(null)
    const createError    = ref('')

    const slotCheckLoading = ref(false)
    const noSlotsBlocking  = ref(false)
    const slotInfo = ref({ slotsPurchased: 0, slotsUsed: 0, slotsAvailable: 0, schoolId: null })

    const waAccounts = ref([])
    const waAccountsLoading = ref(false)

    const groups = ref([])
    const groupsLoading = ref(false)
    const groupSearch = ref('')

    const groupItems = computed(() => groups.value)

    watch(() => props.modelValue, async (open) => {
      if (!open) return
      slotCheckLoading.value = true
      noSlotsBlocking.value  = false
      try {
        const params = props.schoolId ? { school_id: props.schoolId } : {}
        const [slotRes, waRes, groupRes] = await Promise.all([
          axios.get('/api/agents/slot-check', { params }),
          props.schoolId ? axios.get(`/api/schools/${props.schoolId}/whatsapp-accounts`) : Promise.resolve({ data: { accounts: [] } }),
          axios.get('/api/agents/groups', { params }),
        ])
        slotInfo.value = slotRes.data
        waAccounts.value = waRes.data.accounts || []
        groups.value = groupRes.data.groups || []
        // Comms agents don't consume slots — only block on no-slots for worker mode
        noSlotsBlocking.value = !isComms.value && slotRes.data.slotsAvailable === 0
      } catch {
        // If check fails, allow the dialog to proceed — server will reject if truly no slots
      } finally {
        slotCheckLoading.value = false
      }
    })

    const stepLabels = ['Identity & Role', 'AI Engine']

    const form = ref({
      name: '', agentType: 'worker', category: null, groupName: '', roleBrief: '',
      waPhoneNumberId: '', agentGroupIds: [],
      llm_provider: 'anthropic', llm_model: 'claude-haiku-4-5-20251001', api_key: '',
    })

    const isComms = computed(() => form.value.agentType === 'comms')

    const categories = [
      { label: 'Class',       value: 'class' },
      { label: 'Grade',       value: 'grade' },
      { label: 'Sport',       value: 'sport' },
      { label: 'Extramural',  value: 'extramural' },
    ]
    const llmProviders = [
      { label: 'Anthropic (Claude)', value: 'anthropic' },
      { label: 'OpenAI (GPT)',       value: 'openai' },
      { label: 'Local / Ollama',     value: 'ollama' },
    ]
    const modelsByProvider = {
      anthropic: [
        { label: 'Claude Haiku 4.5 (Fast, recommended)', value: 'claude-haiku-4-5-20251001' },
        { label: 'Claude Sonnet 4.6 (Balanced)',         value: 'claude-sonnet-4-6' },
        { label: 'Claude Opus 4.6 (Most capable)',       value: 'claude-opus-4-6' },
      ],
      openai: [
        { label: 'GPT-4o Mini (Fast)', value: 'gpt-4o-mini' },
        { label: 'GPT-4o',             value: 'gpt-4o' },
      ],
      ollama: [
        { label: 'Llama 3',  value: 'llama3' },
        { label: 'Mistral',  value: 'mistral' },
        { label: 'Phi-3',    value: 'phi3' },
      ],
    }

    const availableModels   = computed(() => modelsByProvider[form.value.llm_provider] || [])
    const apiKeyPlaceholder = computed(() => form.value.llm_provider === 'anthropic' ? 'sk-ant-api03-...' : 'sk-...')
    const groupPlaceholder  = computed(() => ({
      class: 'e.g. Grade 4A', grade: 'e.g. Grade 7',
      sport: 'e.g. U14 Rugby', extramural: 'e.g. Swimming',
    }[form.value.category] || 'e.g. Grade 4A'))

    const required = v => !!v || 'Required'

    const nextStep = async () => {
      const formRef = step.value === 1 ? form1 : form2
      if (formRef?.value) {
        const { valid } = await formRef.value.validate()
        if (!valid) return
      }
      step.value++
    }

    const createAndSelectGroup = async (name) => {
      if (!name?.trim()) return
      groupsLoading.value = true
      try {
        const params = props.schoolId ? { school_id: props.schoolId } : {}
        const { data } = await axios.post('/api/agents/groups', { name: name.trim() }, { params })
        const newGroup = data.group
        if (!groups.value.find(g => g.id === newGroup.id)) {
          groups.value.push(newGroup)
        }
        if (!form.value.agentGroupIds.includes(newGroup.id)) {
          form.value.agentGroupIds = [...form.value.agentGroupIds, newGroup.id]
        }
        groupSearch.value = ''
      } catch {
        // silent — group create failure is non-fatal
      } finally {
        groupsLoading.value = false
      }
    }

    const resetForm = () => {
      step.value = 1
      showApiKey.value = false
      createError.value = ''
      noSlotsBlocking.value = false
      createdAgent.value = null
      groupSearch.value = ''
      form.value = {
        name: '', agentType: 'worker', category: null, groupName: '', roleBrief: '',
        waPhoneNumberId: '', agentGroupIds: [],
        llm_provider: 'anthropic', llm_model: 'claude-haiku-4-5-20251001', api_key: '',
      }
    }

    const close = () => {
      emit('update:modelValue', false)
      if (createdAgent.value) emit('created', createdAgent.value)
      resetForm()
    }

    const goToBilling = () => {
      emit('update:modelValue', false)
      resetForm()
      const sid = slotInfo.value.schoolId || props.schoolId
      if (sid) {
        router.push(`/admin/schools/${sid}?tab=billing`)
      } else {
        router.push({ path: router.currentRoute.value.path, query: { tab: 'billing' } })
      }
    }

    const finish = () => {
      emit('update:modelValue', false)
      emit('created', createdAgent.value)
      resetForm()
    }

    const submit = async () => {
      if (form2.value) {
        const { valid } = await form2.value.validate()
        if (!valid) return
      }
      loading.value = true
      createError.value = ''
      try {
        const systemPrompt = isComms.value
          ? buildDefaultCommsPrompt(form.value.name, form.value.roleBrief)
          : buildDefaultSystemPrompt(form.value.name, form.value.groupName, form.value.category, form.value.roleBrief)
        const agentConfig = {
          plugins: ['knowledge_base', 'memory', 'calendar'],
          llmConfig: {
            provider: form.value.llm_provider,
            model: form.value.llm_model,
            apiKey: form.value.api_key || undefined,
          },
          allow_outbound_messaging: true,
        }
        const params = {}
        if (props.schoolId) params.school_id = props.schoolId

        const payload = {
          name: form.value.name,
          agentType: form.value.agentType,
          systemPromptOverride: systemPrompt,
          agentConfig,
        }
        if (!isComms.value) {
          payload.category    = form.value.category
          payload.groupName   = form.value.groupName
          if (form.value.agentGroupIds?.length) payload.agentGroupIds = form.value.agentGroupIds
        } else {
          payload.waPhoneNumberId = form.value.waPhoneNumberId
        }

        const { data } = await axios.post('/api/agents', payload, { params })

        createdAgent.value = data.agent
        step.value = 3
      } catch (err) {
        const code = err.response?.data?.code
        const msg  = err.response?.data?.error || 'Failed to create agent. Please try again.'
        if (code === 'NO_SLOTS') {
          createError.value = 'No agent slots available.'
        } else {
          createError.value = msg
        }
      } finally {
        loading.value = false
      }
    }

    return {
      step, stepLabels, form1, form2, form, loading,
      showApiKey, createdAgent, createError,
      slotCheckLoading, noSlotsBlocking, slotInfo,
      categories, llmProviders, availableModels,
      apiKeyPlaceholder, groupPlaceholder, required,
      isComms, waAccounts, waAccountsLoading,
      groups, groupsLoading, groupSearch, groupItems,
      nextStep, close, finish, submit, goToBilling, createAndSelectGroup,
    }
  }
}
</script>

<style scoped>
.dialog-title { font-size: 1.2rem; font-weight: 700; color: #1a1a2e; }
.field-label {
  font-size: 0.78rem; font-weight: 600; color: #555; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.05em;
}
</style>
